import { DatePipe, DecimalPipe } from '@angular/common';
import { TranslateService } from '@ngx-translate/core';
import { ConversionService } from '@cosmos/angular/utils';
import { ColumnDefinition } from './column-definition.model';

export interface ColumnValueDeps {
  datePipe: DatePipe;
  decimalPipe: DecimalPipe;
  translate: TranslateService;
  conversion?: ConversionService;
}

/**
 * Resolves the display string for one cell. Extracted verbatim from
 * DataTableComponent.findColumnValue so the table render path and any other
 * consumer (e.g. copy-to-spreadsheet) derive cell text the same way.
 */
export function resolveColumnValue(
  element: unknown,
  column: ColumnDefinition,
  deps: ColumnValueDeps,
): string {
  switch (column.type) {
    case 'numArray': {
      const dp = deps.decimalPipe;
      return <string>column.name.split('.').reduce(function (acc: any, cur, index, array) {
        if (acc[cur].length == 0) return '-';
        else if (index < array.length - 1) return acc[cur];
        else return dp.transform(acc[cur][acc[cur].length - 1], '1.0-2');
      }, element);
    }
    case 'arrayLength': {
      const lastElement = column.name.split('.').reduce(function (acc: any, cur) {
        if (acc[cur]?.length == 0 || !acc[cur]) return '-';
        return acc[cur];
      }, element);
      return lastElement == '-' ? '-' : lastElement.length;
    }
    case 'stringArray': {
      return <string>column.name.split('.').reduce(function (prev: any, curr, currentIndex) {
        if (currentIndex != 0) {
          const result: any[] = [];
          prev.forEach((el: any) => result.push(el[curr]));
          return result;
        }
        return prev[curr];
      }, element);
    }
    case 'date': {
      const dp = deps.datePipe;
      return <string>column.name.split('.').reduce(function (acc, cur, index, array) {
        cur = cur.split('?')[0];
        if (acc == null) return '-';
        // this is to chain more than 1 depth of objects
        else if (index < array.length - 1) return acc[cur];
        return dp.transform(acc[cur], 'dd-MM-yyyy HH:mm:ss');
      }, element);
    }
    case 'dateArray': {
      const dp = deps.datePipe;
      return <string>column.name.split('.').reduce(function (acc: any, cur, index, array) {
        cur = cur.split('?')[0];
        if (acc[cur].length == 0) return '-';
        else if (index < array.length - 1) return acc[cur];
        else return dp.transform(acc[cur][acc[cur].length - 1], 'dd-MM-yyyy HH:mm:ss');
      }, element);
    }
    case 'dateArrayFirst': {
      const dp = deps.datePipe;
      return <string>column.name.split('.').reduce(function (acc: any, cur) {
        cur = cur.split('?')[0];
        if (acc[cur].length == 0) return '-';
        return dp.transform(acc[cur][0], 'dd-MM-yyyy HH:mm:ss');
      }, element);
    }
    case 'dateArrayDiff': {
      const ts = deps.translate;
      return <string>column.name.split('.').reduce(function (acc: any, cur) {
        cur = cur.split('?')[0];
        if (acc[cur].length > 1) {
          const timeDiff = new Date(acc[cur][acc[cur].length - 1]).getTime() - new Date(acc[cur][0]).getTime();
          const daysDiff = Math.round((timeDiff * 10) / (1000 * 60 * 60 * 24)) / 10;
          return daysDiff + ' ' + ts.instant('units.time.days');
        }
        return '-';
      }, element);
    }
    case 'date-simple': {
      const dp = deps.datePipe;
      return <string>column.name.split('.').reduce(function (acc: any, cur) {
        if (acc == null) return '-';
        return dp.transform(acc[cur], 'dd-MM-yyyy');
      }, element);
    }
    case 'transmission': {
      const cs = deps.conversion;
      return <string>column.name.split('.').reduce(function (acc: any, cur) {
        if (!Array.isArray(acc[cur]) || acc[cur]?.length == 0) return '-';
        const t = acc[cur][acc[cur].length - 1];
        return cs.getTransmissionInfo(t)[2] + ' - (RSSI: ' + t.rssi + ' - SNR: ' + t.snr + ')';
      }, element);
    }
    case 'boolean': {
      const ts = deps.translate;
      return <string>column.name.split('.').reduce(function (acc: any, cur) {
        if (acc == null) return '?';
        return acc[cur] === true ? ts.instant('boolean.yes') : ts.instant('boolean.no');
      }, element);
    }
    case 'highlight-negative': {
      const dp = deps.decimalPipe;
      return <string>column.name.split('.').reduce(function (acc: any, cur) {
        return dp.transform(acc[cur], '1.0-2');
      }, element);
    }
    case 'number': {
      const dp = deps.decimalPipe;
      const raw = column.name.split('.').reduce((acc: any, cur) => {
        cur = cur.split('?')[0];
        return acc == null ? null : acc[cur];
      }, element);
      return raw == null ? '-' : (dp.transform(raw, '1.0-2') ?? '-');
    }
    case 'tooltip': {
      return <string>column.name.split('.').reduce(function (acc: any, cur) {
        if (acc == null) return '-';
        return acc[cur]['value'];
      }, element);
    }
    case 'translate': {
      const ts = deps.translate;
      const prefix = column.translatePrefix ?? '';
      return <string>column.name.split('.').reduce(function (acc: any, cur) {
        if (acc == null) return '-';
        const raw = acc[cur];
        return raw == null ? '-' : ts.instant(prefix + raw);
      }, element);
    }
    default: {
      return <string>column.name.split('.').reduce(function (acc, cur, index, array) {
        cur = cur.split('?')[0];
        if (acc == null) return '-';
        // this is to chain more than 1 depth of objects
        else if (index < array.length - 1) return acc[cur];
        return acc[cur];
      }, element);
    }
  }
}
